import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { auth } from '../utils/auth';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Scale, ArrowLeft, Lock, CheckCircle, MessageCircle, Clock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import ChatMessages from '../components/ChatMessages';
import { casesApi } from '../services';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CaseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = auth.getUser();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [activeTab, setActiveTab] = useState('details');

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    fetchCase();
  }, [id]);

  const fetchCase = async () => {
    try {
      const token = auth.getToken();
      const response = await axios.get(`${API}/cases/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCaseData(response.data);
    } catch (error) {
      toast.error('Kunne ikke hente sak');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    setPaying(true);
    try {
      const originUrl = window.location.origin;
      
      const response = await axios.post(`${API}/payments/checkout/session`, {
        package_id: 'unlock_answer',
        origin_url: originUrl,
        metadata: {
          case_id: id,
          user_id: user.id,
          email: user.email
        }
      });
      
      // Redirect to Stripe Checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Kunne ikke starte betaling. Prøv igjen.');
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F2EE] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-slate-900 animate-spin" />
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="min-h-screen bg-[#F4F2EE] flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-600">Sak ikke funnet</p>
          <Button onClick={() => navigate('/cases')} className="mt-4">
            Tilbake til saker
          </Button>
        </div>
      </div>
    );
  }

  const hasAnswer = caseData.lawyer_response_preview || caseData.lawyer_response;
  const isPaid = caseData.payment_status === 'COMPLETED';
  const lawyerInfo = {
    name: caseData.lawyer_name || 'Advokat',
    email: caseData.lawyer_email,
    specialization: 'Juridisk rådgivning',
    years_experience: 10,
    rating: 4.8,
    review_count: 45
  };

  return (
    <div className="min-h-screen bg-[#F4F2EE]">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-6">
          <Button
            onClick={() => navigate('/cases')}
            variant="outline"
            size="sm"
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Tilbake til mine saker
          </Button>
          
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2">{caseData.topic}</h1>
              <p className="text-slate-600">{caseData.intake_summary}</p>
            </div>
            
            {/* Status Badge */}
            <div className="flex flex-col gap-2">
              {caseData.status === 'OFFER_SENT' && caseData.offer_id && (
                <Button
                  onClick={() => navigate(`/offer/${caseData.offer_id}`)}
                  className="bg-slate-900 hover:bg-slate-800 text-white"
                >
                  <Clock className="w-4 h-4 mr-2" />
                  Se tilbud fra advokat
                </Button>
              )}
              {!hasAnswer && !caseData.offer_id && (
                <span className="px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-sm font-bold flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Venter på advokat
                </span>
              )}
              {hasAnswer && !isPaid && (
                <span className="px-4 py-2 bg-emerald-100 text-emerald-700 rounded-full text-sm font-bold flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Svar mottatt!
                </span>
              )}
              {isPaid && (
                <span className="px-4 py-2 bg-slate-100 text-slate-800 rounded-full text-sm font-bold flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Betalt
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex gap-6">
            <button
              onClick={() => setActiveTab('details')}
              className={`py-4 px-2 border-b-2 font-medium transition-colors ${
                activeTab === 'details'
                  ? 'border-slate-600 text-slate-900'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              Detaljer
            </button>
            <button
              onClick={() => setActiveTab('chat')}
              className={`py-4 px-2 border-b-2 font-medium transition-colors ${
                activeTab === 'chat'
                  ? 'border-slate-600 text-slate-900'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              <MessageCircle className="w-4 h-4 inline mr-2" />
              Chat med advokat
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        
        {activeTab === 'details' && (
          <div className="space-y-6">
            
            {/* Offer Accepted Banner */}
            {caseData.status === 'IN_PROGRESS' && caseData.requires_pro_plan && (
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl border-2 border-purple-200 p-6">
                <div className="flex items-start gap-4">
                  <CheckCircle className="w-8 h-8 text-purple-600 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-purple-900 mb-2">
                      🎉 Tilbud akseptert - Du er nå oppgradert til Pro!
                    </h3>
                    <p className="text-purple-800 mb-3">
                      Advokaten jobber nå med saken din. Du har full tilgang til:
                    </p>
                    <ul className="space-y-1 text-sm text-purple-700">
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-purple-600"></span>
                        Direktemelding med advokat
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-purple-600"></span>
                        Timeregistrering og dokumentsporing
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-purple-600"></span>
                        Løpende oppdateringer på sakens fremgang
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
            
            {/* Answer Section */}
            {hasAnswer && (
              <div className="bg-white rounded-2xl border-2 border-slate-200 overflow-hidden">
                <div className="bg-gradient-to-r from-indigo-600 to-blue-600 p-6 text-white">
                  <div className="flex items-center gap-3 mb-2">
                    <Scale className="w-6 h-6" />
                    <h2 className="text-2xl font-bold">Juridisk vurdering</h2>
                  </div>
                  <p className="text-indigo-100">Svar fra autorisert advokat</p>
                </div>
                
                {/* Lawyer Profile */}
                <div className="bg-gradient-to-br from-slate-50 to-blue-50 p-6 border-b border-slate-200">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-blue-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                      {lawyerInfo.name.charAt(0)}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-slate-900">{lawyerInfo.name}</h3>
                      <p className="text-sm text-slate-600">{lawyerInfo.specialization}</p>
                      {lawyerInfo.rating && (
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex items-center gap-1">
                            {[...Array(5)].map((_, i) => (
                              <span key={i} className="text-yellow-400">
                                {i < Math.floor(lawyerInfo.rating) ? '⭐' : '☆'}
                              </span>
                            ))}
                          </div>
                          <span className="text-xs text-slate-500">
                            ({lawyerInfo.review_count} anmeldelser)
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="p-8">
                  {!isPaid ? (
                    <>
                      {/* Preview */}
                      <div className="mb-8">
                        <h4 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">Advokat sitt svar:</h4>
                        <div className="relative">
                          <p className="text-lg text-slate-700 leading-relaxed whitespace-pre-wrap">
                            {caseData.lawyer_response_preview || caseData.lawyer_response?.substring(0, 300) + '...'}
                          </p>
                          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white pointer-events-none" style={{ top: '60%' }} />
                        </div>
                      </div>
                      
                      {/* Unlock CTA */}
                      <div className="bg-gradient-to-br from-slate-50 to-indigo-50 rounded-xl p-8 text-center border-2 border-slate-200 shadow-sm">
                        <div className="w-20 h-20 bg-slate-900 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Lock className="w-10 h-10 text-white" />
                        </div>
                        <h3 className="text-2xl font-bold text-slate-900 mb-3">
                          Lås opp fullstendig juridisk rådgivning
                        </h3>
                        <p className="text-slate-600 mb-6 max-w-md mx-auto">
                          Få tilgang til hele den profesjonelle juridiske vurderingen fra {lawyerInfo.name}, 
                          konkrete anbefalinger, videre steg og mulighet til å chatte med advokaten.
                        </p>
                        
                        <div className="bg-white rounded-xl p-6 mb-6 max-w-md mx-auto">
                          <div className="text-5xl font-bold text-slate-900 mb-2">499 kr</div>
                          <div className="text-sm text-slate-500 mb-4">Engangsbeløp • Ingen skjulte kostnader</div>
                          <div className="space-y-2 text-left text-sm text-slate-600">
                            <div className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-emerald-500" />
                              <span>Komplett svar fra advokat</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-emerald-500" />
                              <span>Juridisk begrunnelse</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-emerald-500" />
                              <span>Konkrete anbefalinger</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-emerald-500" />
                              <span>Chat med advokat</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-emerald-500" />
                              <span>Signert av autorisert advokat</span>
                            </div>
                          </div>
                        </div>
                        
                        <Button
                          onClick={handlePayment}
                          disabled={paying}
                          className="bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-lg px-12 py-6 rounded-xl w-full max-w-md shadow-sm"
                        >
                          {paying ? (
                            <>
                              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                              Behandler betaling...
                            </>
                          ) : (
                            <>
                              <CheckCircle className="w-5 h-5 mr-2" />
                              Lås opp for 499 kr
                            </>
                          )}
                        </Button>
                        
                        <div className="flex items-center justify-center gap-6 mt-6 text-xs text-slate-500">
                          <span>✓ Sikker betaling med Stripe</span>
                          <span>✓ 14 dagers angrerett</span>
                          <span>✓ Norsk advokat</span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Full Answer */}
                      <div className="prose prose-lg max-w-none">
                        <h4 className="text-sm font-semibold text-slate-700 mb-4 uppercase tracking-wide">Komplett juridisk vurdering:</h4>
                        <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                          {caseData.lawyer_response_full || caseData.lawyer_response}
                        </p>
                      </div>
                      
                      <div className="mt-8 pt-6 border-t border-slate-200 flex items-center justify-between">
                        <p className="text-sm text-slate-600">
                          ✓ Svar godkjent av autorisert advokat
                        </p>
                        <p className="text-sm text-slate-500">
                          Betalt {new Date(caseData.paid_at || Date.now()).toLocaleDateString('no-NO')}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
            
            {/* Waiting State */}
            {!hasAnswer && (
              <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
                <div className="w-24 h-24 bg-gradient-to-br from-amber-100 to-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Clock className="w-12 h-12 text-amber-600 animate-pulse" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3">
                  Din sak er under behandling
                </h3>
                <p className="text-slate-600 mb-4 max-w-md mx-auto">
                  En autorisert advokat ser på saken din. Du får beskjed når den juridiske vurderingen er klar.
                </p>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 rounded-lg">
                  <Clock className="w-4 h-4 text-amber-600" />
                  <span className="text-sm text-amber-900 font-medium">
                    Forventet svar innen 24-48 timer
                  </span>
                </div>
                
                <div className="mt-8 pt-8 border-t border-slate-200">
                  <p className="text-sm text-slate-600 mb-4">I mellomtiden kan du:</p>
                  <div className="flex gap-3 justify-center">
                    <Button
                      onClick={() => setActiveTab('chat')}
                      variant="outline"
                      size="sm"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Send melding til advokat
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'chat' && (
          <div>
            {!isPaid ? (
              <div className="bg-white rounded-xl border-2 border-slate-200 p-12 text-center shadow-sm">
                <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-10 h-10 text-slate-900" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3">
                  Chat er låst
                </h3>
                <p className="text-slate-600 mb-6 max-w-md mx-auto">
                  For å chatte med advokaten må du først låse opp svaret ved å betale 499 kr.
                </p>
                <Button
                  onClick={() => setActiveTab('details')}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  Se svar og lås opp
                </Button>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" style={{ height: '600px' }}>
                <ChatMessages caseId={id} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CaseDetail;
